import * as ed from "@noble/ed25519";
import { sha256, sha512 } from "@noble/hashes/sha2";
import type { LedgerEntry } from "../net/api";

// @noble/ed25519 v2 needs a sync sha512 for verify(); wire noble-hashes once.
ed.etc.sha512Sync = (...m) => sha512(ed.etc.concatBytes(...m));

export type VerifyResult = { ok: true } | { ok: false; code: string; seq: number };

const ZERO = "00".repeat(32);

function bytesToHex(b: Uint8Array): string { return [...b].map((x) => x.toString(16).padStart(2, "0")).join(""); }
function b64(s: string): Uint8Array { return Uint8Array.from(atob(s), (c) => c.charCodeAt(0)); }

/** seq, asset_id, kind, from, to, payload, hex(prev_hash), created_at — joined by 0x00. Mirrors ledger.Canonical. */
export function canonicalBytes(e: LedgerEntry): Uint8Array {
  const fields = [String(e.seq), e.asset_id, e.kind, e.from ?? "", e.to, e.payload ?? "", e.prev_hash.toLowerCase(), e.created_at];
  return new TextEncoder().encode(fields.join("\u0000"));
}

const VISIBILITY = new Set(["private", "shared", "public"]);
const LICENSE = new Set(["", "cc0", "cc-by", "world-only"]);

function validPolicy(payload: string): boolean {
  const i = payload.indexOf("/");
  if (i < 0) return false;
  const v = payload.slice(0, i), l = payload.slice(i + 1);
  if (!VISIBILITY.has(v) || !LICENSE.has(l)) return false;
  return v === "private" || l !== "";
}

function signer(e: LedgerEntry): string { return e.kind === "mint" ? e.to : e.from; }

export function verifyChain(entries: LedgerEntry[], keys: Record<string, string>): VerifyResult {
  if (entries.length === 0) return { ok: false, code: "EMPTY", seq: 0 };
  const first = entries[0]!;
  if (first.kind !== "mint" || first.seq !== 1 || first.prev_hash.toLowerCase() !== ZERO) return { ok: false, code: "FIRST_NOT_MINT", seq: first.seq };
  for (let i = 0; i < entries.length; i++) {
    const e = entries[i]!;
    if (e.asset_id !== first.asset_id) return { ok: false, code: "ASSET_MISMATCH", seq: e.seq };
    if (i > 0) {
      const prev = entries[i - 1]!;
      if (e.kind === "mint") return { ok: false, code: "FIRST_NOT_MINT", seq: e.seq };
      if (e.seq !== prev.seq + 1) return { ok: false, code: "BAD_SEQ", seq: e.seq };
      if (e.prev_hash.toLowerCase() !== prev.hash.toLowerCase()) return { ok: false, code: "BROKEN_CHAIN", seq: e.seq };
      if (e.from !== prev.to) return { ok: false, code: "OWNER_MISMATCH", seq: e.seq };
    }
    if (e.kind === "policy") {
      if (e.from !== e.to) return { ok: false, code: "POLICY_OWNER_CHANGE", seq: e.seq };
      if (!validPolicy(e.payload)) return { ok: false, code: "BAD_PAYLOAD", seq: e.seq };
    } else if (e.payload) {
      return { ok: false, code: "BAD_PAYLOAD", seq: e.seq };
    }
    const hash = sha256(canonicalBytes(e));
    if (bytesToHex(hash) !== e.hash.toLowerCase()) return { ok: false, code: "BAD_HASH", seq: e.seq };
    const key = keys[signer(e)];
    if (!key || key !== e.signer_pubkey) return { ok: false, code: "UNKNOWN_SIGNER", seq: e.seq };
    if (!ed.verify(b64(e.signature), hash, b64(e.signer_pubkey))) return { ok: false, code: "BAD_SIGNATURE", seq: e.seq };
  }
  return { ok: true };
}
